import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, COMPANY, telLink } from "../lib/site";
import { motion, AnimatePresence } from "framer-motion";

export default function Header({ onOpenLead }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-2xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={COMPANY.logo}
            alt="S Mahadeva Properties"
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
          />
          <span className="sr-only">S Mahadeva Properties</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === "/"}
              data-testid={`nav-${l.name.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-[0.2em] uppercase font-light transition-colors duration-300 ${
                  isActive
                    ? "text-[#D4AF37]"
                    : "text-white/70 hover:text-[#D4AF37]"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={telLink(COMPANY.phones[1])}
            data-testid="header-call-btn"
            className="flex items-center gap-2 text-sm text-white/80 hover:text-[#D4AF37] transition-colors"
          >
            <Phone className="w-4 h-4" strokeWidth={1.4} />
            <span className="font-mono text-xs">{COMPANY.phones[1]}</span>
          </a>
          <button
            data-testid="header-enquire-btn"
            onClick={onOpenLead}
            className="bg-[#D4AF37] text-black font-medium tracking-[0.15em] uppercase text-xs px-6 py-3 hover:bg-[#B3932E] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.45)]"
          >
            Enquire Now
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black backdrop-blur-2xl border-t border-white/5"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.path === "/"}
                  data-testid={`mobile-nav-${l.name.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm tracking-[0.25em] uppercase ${
                      isActive ? "text-[#D4AF37]" : "text-white/80"
                    }`
                  }
                >
                  {l.name}
                </NavLink>
              ))}
              <button
                data-testid="mobile-enquire-btn"
                onClick={() => {
                  setOpen(false);
                  onOpenLead?.();
                }}
                className="mt-2 bg-[#D4AF37] text-black font-medium tracking-[0.15em] uppercase text-xs px-6 py-4"
              >
                Book Free Site Visit
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
